// Tiny Markdown → HTML for the customer product detail page.
// Source comes from admin-only input (CF Access protected), but we still
// HTML-escape every literal char before applying inline patterns so a stray
// `<script>` in copy can never run.
//
// Supported subset:
//   - Paragraphs (blank-line separated)
//   - `# / ## / ###` headings (mapped to h2/h3/h4 to fit the page hierarchy)
//   - Bullet lists `- item` / `* item`
//   - Ordered lists `1. item`
//   - **bold**, *italic*, `code`
//   - [text](url), ![alt](url)
//   - Horizontal rule `---`

export function renderMarkdown(src: string): string {
  if (!src || !src.trim()) return ''

  const lines = src.replace(/\r\n?/g, '\n').split('\n')
  const out: string[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]!

    // blank line
    if (!line.trim()) { i++; continue }

    // hr
    if (/^\s*---\s*$/.test(line)) { out.push('<hr/>'); i++; continue }

    // heading
    const h = /^\s*(#{1,3})\s+(.+?)\s*$/.exec(line)
    if (h) {
      const level = h[1]!.length
      const tag = level === 1 ? 'h2' : level === 2 ? 'h3' : 'h4'
      out.push(`<${tag}>${inline(h[2]!)}</${tag}>`)
      i++
      continue
    }

    // unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i]!)) {
        items.push(`<li>${inline(lines[i]!.replace(/^\s*[-*]\s+/, ''))}</li>`)
        i++
      }
      out.push(`<ul>${items.join('')}</ul>`)
      continue
    }

    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i]!)) {
        items.push(`<li>${inline(lines[i]!.replace(/^\s*\d+\.\s+/, ''))}</li>`)
        i++
      }
      out.push(`<ol>${items.join('')}</ol>`)
      continue
    }

    // standalone image (single line) → wrap so it gets block layout
    if (/^!\[[^\]]*\]\([^)]+\)\s*$/.test(line)) {
      out.push(inline(line))
      i++
      continue
    }

    // paragraph (consume contiguous non-blank lines)
    const para: string[] = [line]
    i++
    while (i < lines.length && lines[i]!.trim() && !startsBlock(lines[i]!)) {
      para.push(lines[i]!)
      i++
    }
    out.push(`<p>${inline(para.join(' '))}</p>`)
  }

  return out.join('\n')
}

function startsBlock(line: string): boolean {
  return /^\s*(#{1,3}\s|[-*]\s|\d+\.\s|---\s*$)/.test(line)
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/'/g, '&#39;')
}

/** Apply inline patterns. We escape FIRST, then re-introduce the markup. */
function inline(raw: string): string {
  let s = escapeHtml(raw)

  // images: ![alt](url) — allow http(s)/relative urls only.
  s = s.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_, alt: string, url: string) => {
    if (!isSafeUrl(url)) return ''
    return `<img src="${escapeAttr(url)}" alt="${escapeAttr(alt)}"/>`
  })

  // links: [text](url)
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, text: string, url: string) => {
    if (!isSafeUrl(url)) return text
    return `<a href="${escapeAttr(url)}" target="_blank" rel="noopener nofollow">${text}</a>`
  })

  // code spans
  s = s.replace(/`([^`]+)`/g, (_, code: string) => `<code>${code}</code>`)

  // bold (must run before italic so `**x**` isn't gobbled by `*x*`)
  s = s.replace(/\*\*([^*]+)\*\*/g, (_, t: string) => `<strong>${t}</strong>`)
  s = s.replace(/\*([^*]+)\*/g, (_, t: string) => `<em>${t}</em>`)

  return s
}

function isSafeUrl(u: string): boolean {
  if (!u) return false
  if (u.startsWith('/')) return true
  return /^https?:\/\//i.test(u)
}
